import React, { useState, useEffect } from 'react';
import { globalCategoryFieldAPI } from '../services/api';
import { Plus, Edit, Trash2, Save, X, Search, Globe, Languages, TrendingUp, Key, Tag, Database } from 'lucide-react';
import GlobalFieldSamplesModal from './GlobalFieldSamplesModal';

const GlobalCategoryFieldsManagement = () => {
  const [globalFields, setGlobalFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSamplesModal, setShowSamplesModal] = useState(false);
  const [selectedFieldForSamples, setSelectedFieldForSamples] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [supportedLanguages, setSupportedLanguages] = useState({});
  const [formData, setFormData] = useState({
    key: '',
    aliasKey: '',
    labels: { en: '' },
    fieldType: 'text',
    isRequired: false,
    fieldConfig: {},
    validation: '',
    minLength: '',
    maxLength: '',
    sampleValues: {},
    defaultValues: {},
    category: '',
    description: ''
  });
  const [availableLanguages] = useState([
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'Hindi' },
    { code: 'bn', name: 'Bengali' },
    { code: 'mr', name: 'Marathi' },
    { code: 'te', name: 'Telugu' },
    { code: 'ta', name: 'Tamil' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'kn', name: 'Kannada' },
    { code: 'pa', name: 'Punjabi' }
  ]);

  useEffect(() => {
    fetchGlobalFields();
    fetchSupportedLanguages();
  }, [selectedCategory, selectedLanguage]);

  const fetchGlobalFields = async () => {
    setLoading(true);
    try {
      const params = {
        language: selectedLanguage,
        ...(selectedCategory !== 'all' && { category: selectedCategory })
      };
      const response = await globalCategoryFieldAPI.getAll(params);
      setGlobalFields(response.data.data.fields || []);
    } catch (err) {
      setError('Failed to fetch global fields');
      console.error('Error fetching global fields:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportedLanguages = async () => {
    try {
      const response = await globalCategoryFieldAPI.getSupportedLanguages();
      setSupportedLanguages(response.data.data.languages || {});
    } catch (err) {
      console.error('Error fetching supported languages:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      key: '',
      aliasKey: '',
      labels: { en: '' },
      fieldType: 'text',
      isRequired: false,
      fieldConfig: {},
      validation: '',
      minLength: '',
      maxLength: '',
      sampleValues: {},
      defaultValues: {},
      category: '',
      description: ''
    });
    setEditingField(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingField) {
        await globalCategoryFieldAPI.update(editingField.id, formData);
      } else {
        await globalCategoryFieldAPI.create(formData);
      }
      await fetchGlobalFields();
      resetForm();
      setShowCreateForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save global field');
    }
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setFormData({
      key: field.key,
      aliasKey: field.aliasKey || '',
      labels: field.labels || { en: field.label },
      fieldType: field.fieldType,
      isRequired: field.isRequired,
      fieldConfig: field.fieldConfig || {},
      validation: field.validation || '',
      minLength: field.minLength || '',
      maxLength: field.maxLength || '',
      sampleValues: field.sampleValues || {},
      defaultValues: field.defaultValues || {},
      category: field.category || '',
      description: field.description || ''
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Are you sure you want to delete this global field?')) return;

    try {
      await globalCategoryFieldAPI.delete(fieldId);
      await fetchGlobalFields();
    } catch (err) {
      setError('Failed to delete global field');
    }
  };

  const handleManageSamples = (field) => {
    setSelectedFieldForSamples(field);
    setShowSamplesModal(true);
  };

  const handleCreateDefaults = async () => {
    if (!window.confirm('Create default global fields? This will add common fields like bride_name, groom_name, etc.')) return;
    
    try {
      await globalCategoryFieldAPI.createDefaults();
      await fetchGlobalFields();
    } catch (err) {
      setError('Failed to create default fields');
    }
  };

  const updateLabel = (language, value) => {
    setFormData(prev => ({
      ...prev,
      labels: {
        ...prev.labels,
        [language]: value
      }
    }));
  };

  const updateSampleValue = (language, value) => {
    setFormData(prev => ({
      ...prev,
      sampleValues: {
        ...prev.sampleValues,
        [language]: value
      }
    }));
  };

  const updateDefaultValue = (language, value) => {
    setFormData(prev => ({
      ...prev,
      defaultValues: {
        ...prev.defaultValues,
        [language]: value
      }
    }));
  };

  const addLanguage = (languageCode) => {
    setFormData(prev => ({
      ...prev,
      labels: {
        ...prev.labels,
        [languageCode]: ''
      },
      sampleValues: {
        ...prev.sampleValues,
        [languageCode]: ''
      },
      defaultValues: {
        ...prev.defaultValues,
        [languageCode]: ''
      }
    }));
  };

  const removeLanguage = (languageCode) => {
    if (languageCode === 'en') return; // Don't allow removing English

    setFormData(prev => {
      const newLabels = { ...prev.labels };
      const newSampleValues = { ...prev.sampleValues };
      const newDefaultValues = { ...prev.defaultValues };

      delete newLabels[languageCode];
      delete newSampleValues[languageCode];
      delete newDefaultValues[languageCode];

      return {
        ...prev,
        labels: newLabels,
        sampleValues: newSampleValues,
        defaultValues: newDefaultValues
      };
    });
  };

  const getAvailableLanguagesToAdd = () => {
    const currentLanguages = Object.keys(formData.labels);
    return availableLanguages.filter(lang => !currentLanguages.includes(lang.code));
  };

  const updateFieldConfig = (key, value) => {
    setFormData(prev => ({
      ...prev,
      fieldConfig: {
        ...prev.fieldConfig,
        [key]: value
      }
    }));
  };

  const renderFieldConfiguration = () => {
    const { fieldType } = formData;

    switch (fieldType) {
      case 'image':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.multiple || false}
                  onChange={(e) => updateFieldConfig('multiple', e.target.checked)}
                  className="mr-2"
                />
                Allow Multiple Images
              </label>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Accepted File Types</label>
              <input
                type="text"
                value={formData.fieldConfig.accept || '.jpg,.jpeg,.png,.gif,.webp'}
                onChange={(e) => updateFieldConfig('accept', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder=".jpg,.jpeg,.png,.gif,.webp"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max File Size (MB)</label>
              <input
                type="number"
                value={formData.fieldConfig.maxSize || 5}
                onChange={(e) => updateFieldConfig('maxSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                max="50"
              />
            </div>
          </div>
        );

      case 'pdf':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.multiple || false}
                  onChange={(e) => updateFieldConfig('multiple', e.target.checked)}
                  className="mr-2"
                />
                Allow Multiple PDFs
              </label>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max File Size (MB)</label>
              <input
                type="number"
                value={formData.fieldConfig.maxSize || 10}
                onChange={(e) => updateFieldConfig('maxSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                max="100"
              />
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.multiple || false}
                  onChange={(e) => updateFieldConfig('multiple', e.target.checked)}
                  className="mr-2"
                />
                Allow Multiple Videos
              </label>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Accepted File Types</label>
              <input
                type="text"
                value={formData.fieldConfig.accept || '.mp4,.avi,.mov,.wmv,.webm'}
                onChange={(e) => updateFieldConfig('accept', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder=".mp4,.avi,.mov,.wmv,.webm"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max File Size (MB)</label>
              <input
                type="number"
                value={formData.fieldConfig.maxSize || 50}
                onChange={(e) => updateFieldConfig('maxSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                max="500"
              />
            </div>
          </div>
        );

      case 'date':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Date Format</label>
              <select
                value={formData.fieldConfig.format || 'dd/mm/yyyy'}
                onChange={(e) => updateFieldConfig('format', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="dd/mm/yyyy">DD/MM/YYYY</option>
                <option value="mm/dd/yyyy">MM/DD/YYYY</option>
                <option value="yyyy-mm-dd">YYYY-MM-DD</option>
                <option value="dd-mm-yyyy">DD-MM-YYYY</option>
              </select>
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.allowPast || true}
                  onChange={(e) => updateFieldConfig('allowPast', e.target.checked)}
                  className="mr-2"
                />
                Allow Past Dates
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.allowFuture || true}
                  onChange={(e) => updateFieldConfig('allowFuture', e.target.checked)}
                  className="mr-2"
                />
                Allow Future Dates
              </label>
            </div>
          </div>
        );

      case 'time':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Time Format</label>
              <select
                value={formData.fieldConfig.format || '12h'}
                onChange={(e) => updateFieldConfig('format', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="12h">12 Hour (AM/PM)</option>
                <option value="24h">24 Hour</option>
              </select>
            </div>
          </div>
        );

      case 'select':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.multiple || false}
                  onChange={(e) => updateFieldConfig('multiple', e.target.checked)}
                  className="mr-2"
                />
                Allow Multiple Selection
              </label>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Options (one per line)</label>
              <textarea
                value={formData.fieldConfig.options ? formData.fieldConfig.options.join('\n') : ''}
                onChange={(e) => updateFieldConfig('options', e.target.value.split('\n').filter(opt => opt.trim()))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
            </div>
          </div>
        );

      case 'url':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.requireHttps || false}
                  onChange={(e) => updateFieldConfig('requireHttps', e.target.checked)}
                  className="mr-2"
                />
                Require HTTPS
              </label>
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Rows</label>
              <input
                type="number"
                value={formData.fieldConfig.rows || 4}
                onChange={(e) => updateFieldConfig('rows', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="2"
                max="20"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max Characters</label>
              <input
                type="number"
                value={formData.fieldConfig.maxChars || 500}
                onChange={(e) => updateFieldConfig('maxChars', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
              />
            </div>
          </div>
        );

      case 'audio':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.multiple || false}
                  onChange={(e) => updateFieldConfig('multiple', e.target.checked)}
                  className="mr-2"
                />
                Allow Multiple Audio Files
              </label>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Accepted File Types</label>
              <input
                type="text"
                value={formData.fieldConfig.accept || '.mp3,.wav,.ogg,.m4a'}
                onChange={(e) => updateFieldConfig('accept', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder=".mp3,.wav,.ogg,.m4a"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max File Size (MB)</label>
              <input
                type="number"
                value={formData.fieldConfig.maxSize || 10}
                onChange={(e) => updateFieldConfig('maxSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                max="100"
              />
            </div>
          </div>
        );

      case 'file':
        return (
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.multiple || false}
                  onChange={(e) => updateFieldConfig('multiple', e.target.checked)}
                  className="mr-2"
                />
                Allow Multiple Files
              </label>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Accepted File Types</label>
              <input
                type="text"
                value={formData.fieldConfig.accept || ''}
                onChange={(e) => updateFieldConfig('accept', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder=".pdf,.doc,.docx,.txt"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max File Size (MB)</label>
              <input
                type="number"
                value={formData.fieldConfig.maxSize || 5}
                onChange={(e) => updateFieldConfig('maxSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                max="100"
              />
            </div>
          </div>
        );

      case 'datetime':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Date & Time Format</label>
              <select
                value={formData.fieldConfig.format || 'dd/mm/yyyy hh:mm'}
                onChange={(e) => updateFieldConfig('format', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="dd/mm/yyyy hh:mm">DD/MM/YYYY HH:MM</option>
                <option value="mm/dd/yyyy hh:mm">MM/DD/YYYY HH:MM</option>
                <option value="yyyy-mm-dd hh:mm">YYYY-MM-DD HH:MM</option>
              </select>
            </div>
          </div>
        );

      case 'multiselect':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Options (one per line)</label>
              <textarea
                value={formData.fieldConfig.options ? formData.fieldConfig.options.join('\n') : ''}
                onChange={(e) => updateFieldConfig('options', e.target.value.split('\n').filter(opt => opt.trim()))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max Selections</label>
              <input
                type="number"
                value={formData.fieldConfig.maxSelections || ''}
                onChange={(e) => updateFieldConfig('maxSelections', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                placeholder="Leave empty for unlimited"
              />
            </div>
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Options (one per line)</label>
              <textarea
                value={formData.fieldConfig.options ? formData.fieldConfig.options.join('\n') : ''}
                onChange={(e) => updateFieldConfig('options', e.target.value.split('\n').filter(opt => opt.trim()))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
            </div>
          </div>
        );

      case 'range':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Min Value</label>
                <input
                  type="number"
                  value={formData.fieldConfig.min || 0}
                  onChange={(e) => updateFieldConfig('min', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Max Value</label>
                <input
                  type="number"
                  value={formData.fieldConfig.max || 100}
                  onChange={(e) => updateFieldConfig('max', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Step</label>
              <input
                type="number"
                value={formData.fieldConfig.step || 1}
                onChange={(e) => updateFieldConfig('step', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
              />
            </div>
          </div>
        );

      case 'number':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Min Value</label>
                <input
                  type="number"
                  value={formData.fieldConfig.min || ''}
                  onChange={(e) => updateFieldConfig('min', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="No minimum"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Max Value</label>
                <input
                  type="number"
                  value={formData.fieldConfig.max || ''}
                  onChange={(e) => updateFieldConfig('max', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="No maximum"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.fieldConfig.allowDecimals || false}
                  onChange={(e) => updateFieldConfig('allowDecimals', e.target.checked)}
                  className="mr-2"
                />
                Allow Decimal Numbers
              </label>
            </div>
          </div>
        );

      case 'multiselect-image':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Accepted File Types</label>
              <input
                type="text"
                value={formData.fieldConfig.accept || '.jpg,.jpeg,.png,.gif,.webp'}
                onChange={(e) => updateFieldConfig('accept', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder=".jpg,.jpeg,.png,.gif,.webp"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max File Size per Image (MB)</label>
              <input
                type="number"
                value={formData.fieldConfig.maxSize || 5}
                onChange={(e) => updateFieldConfig('maxSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                max="50"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max Number of Images</label>
              <input
                type="number"
                value={formData.fieldConfig.maxImages || ''}
                onChange={(e) => updateFieldConfig('maxImages', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                placeholder="Leave empty for unlimited"
              />
            </div>
            <p className="text-xs text-gray-500">
              Each image will have title, description, and sequence fields for metadata.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  const filteredFields = globalFields.filter(field => {
    const matchesSearch = field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         field.key.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const categories = [...new Set(globalFields.map(f => f.category).filter(Boolean))];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Globe className="w-6 h-6 mr-2" />
            Global Category Fields
          </h1>
          <p className="text-gray-600 mt-1">Manage reusable, multi-language category fields</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleCreateDefaults}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Create Defaults
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Global Field
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-64">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search fields..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        
        <div className="min-w-48">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-48">
          <div className="relative">
            <Languages className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {Object.entries(supportedLanguages).map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Global Fields List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredFields.length === 0 ? (
          <div className="text-center py-12">
            <Globe className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No global fields found</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create First Global Field
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredFields.map((field) => (
              <div key={field.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <Key className="w-4 h-4 text-gray-400" />
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">{field.key}</code>
                      </div>
                      {field.aliasKey && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">alias:</span>
                          <span className="text-sm text-gray-700 italic">{field.aliasKey}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-2">
                        <Tag className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">{field.label}</span>
                      </div>
                      {field.category && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {field.category}
                        </span>
                      )}
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {field.fieldType}
                      </span>
                      {field.isRequired && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                          Required
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        Used {field.usageCount} times
                      </span>
                    </div>
                    {field.description && (
                      <p className="text-sm text-gray-600 mt-1">{field.description}</p>
                    )}
                    {field.sampleValue && (
                      <p className="text-xs text-gray-500 mt-1">
                        Sample: <span className="italic">{field.sampleValue}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(field)}
                      className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded border border-blue-200"
                      title="Edit Field"
                    >
                      <Edit className="w-3 h-3" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleManageSamples(field)}
                      className="flex items-center space-x-1 px-3 py-1 text-sm text-green-600 hover:text-green-800 hover:bg-green-50 rounded border border-green-200"
                      title={field.fieldType === 'multiselect-image' ? 'Image/Values' : 'Manage Samples'}
                    >
                      <Database className="w-3 h-3" />
                      <span>{field.fieldType === 'multiselect-image' ? 'Image/Values' : 'Manage Samples'}</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {editingField ? 'Edit Global Field' : 'Create Global Field'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Key * <span className="text-xs text-gray-500">(snake_case)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    required
                    pattern="^[a-z][a-z0-9_]*[a-z0-9]$"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="bride_name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alias Key <span className="text-xs text-gray-500">(optional, human-readable)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.aliasKey}
                    onChange={(e) => setFormData({ ...formData, aliasKey: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Bride Name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                  <select
                    value={formData.fieldType}
                    onChange={(e) => setFormData({ ...formData, fieldType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="time">Time</option>
                    <option value="datetime">Date & Time</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="url">URL</option>
                    <option value="image">Image</option>
                    <option value="pdf">PDF</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                    <option value="file">File</option>
                    <option value="select">Select</option>
                    <option value="multiselect">Multi-Select</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="radio">Radio Button</option>
                    <option value="color">Color</option>
                    <option value="range">Range/Slider</option>
                    <option value="password">Password</option>
                    <option value="multiselect-image">Multi-Select-Image</option>
                  </select>
                </div>
                <div></div>
              </div>

              {/* Field Configuration based on field type */}
              {formData.fieldType && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Field Configuration
                  </label>
                  {renderFieldConfiguration()}
                </div>
              )}

              {/* Multi-language Labels */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Labels (Multi-language) *
                  </label>
                  <div className="flex items-center space-x-2">
                    {getAvailableLanguagesToAdd().length > 0 && (
                      <div className="flex items-center space-x-2">
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              addLanguage(e.target.value);
                              e.target.value = '';
                            }
                          }}
                          className="text-xs px-2 py-1 border border-gray-300 rounded"
                        >
                          <option value="">+ Add Language</option>
                          {getAvailableLanguagesToAdd().map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {lang.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  {Object.entries(formData.labels).map(([code, value]) => {
                    const languageName = availableLanguages.find(lang => lang.code === code)?.name || code.toUpperCase();
                    return (
                      <div key={code} className="flex items-center space-x-2">
                        <span className="w-20 text-xs text-gray-600">{languageName.split(' ')[0]}:</span>
                        <input
                          type="text"
                          value={value || ''}
                          onChange={(e) => updateLabel(code, e.target.value)}
                          required={code === 'en'}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder={code === 'en' ? 'Field Label' : ''}
                        />
                        {code !== 'en' && (
                          <button
                            type="button"
                            onClick={() => removeLanguage(code)}
                            className="px-2 py-1 text-xs text-red-600 hover:text-red-800 border border-red-200 rounded"
                            title="Remove Language"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sample Values */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sample Values (Optional)
                </label>
                <div className="space-y-2">
                  {Object.entries(formData.labels).map(([code, _]) => {
                    const languageName = availableLanguages.find(lang => lang.code === code)?.name || code.toUpperCase();
                    return (
                      <div key={code} className="flex items-center space-x-2">
                        <span className="w-20 text-xs text-gray-600">{languageName.split(' ')[0]}:</span>
                        <input
                          type="text"
                          value={formData.sampleValues[code] || ''}
                          onChange={(e) => updateSampleValue(code, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder={code === 'en' ? 'Sample value' : ''}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Default Values */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Values (Optional)
                </label>
                <div className="space-y-2">
                  {Object.entries(formData.labels).map(([code, _]) => {
                    const languageName = availableLanguages.find(lang => lang.code === code)?.name || code.toUpperCase();
                    return (
                      <div key={code} className="flex items-center space-x-2">
                        <span className="w-20 text-xs text-gray-600">{languageName.split(' ')[0]}:</span>
                        <input
                          type="text"
                          value={formData.defaultValues[code] || ''}
                          onChange={(e) => updateDefaultValue(code, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder={code === 'en' ? 'Default value' : ''}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Validation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Validation Rules (Optional)
                </label>
                <input
                  type="text"
                  value={formData.validation}
                  onChange={(e) => setFormData({ ...formData, validation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="regex:^[a-zA-Z\s]+$ or email or phone"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter validation rules like 'email', 'phone', or 'regex:pattern'
                </p>
              </div>

              {/* Additional Settings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="wedding"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Length</label>
                  <input
                    type="number"
                    value={formData.minLength}
                    onChange={(e) => setFormData({ ...formData, minLength: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Length</label>
                  <input
                    type="number"
                    value={formData.maxLength}
                    onChange={(e) => setFormData({ ...formData, maxLength: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isRequired}
                    onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Required Field</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Description of this field's purpose..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingField ? 'Update' : 'Create'} Field
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Global Field Samples Modal */}
      <GlobalFieldSamplesModal
        isOpen={showSamplesModal}
        onClose={() => {
          setShowSamplesModal(false);
          setSelectedFieldForSamples(null);
        }}
        globalField={selectedFieldForSamples}
      />
    </div>
  );
};

export default GlobalCategoryFieldsManagement;
